// AI for Agents training module for RESIDENCE | eXp Realty.
// Draft content. Check facts and wording before publishing.

export const aiAgentsModule = {
  "id": "aiAgents",
  "title": "AI for Agents",
  "intro": "Simple, everyday ways to use AI to work more efficiently as a real estate agent. Work through each step in order. Paste in the details you have, since the more context AI gets, the better it works, and check every fact before it reaches a client.",
  "sections": [
    {
      "id": "start",
      "title": "Getting Started With AI",
      "steps": [
        {
          "id": "ai-what-it-does",
          "title": "What AI Can and Can't Do for Your Business",
          "body": [
            "AI is a fast first drafter and a tireless assistant. It is not a licensed professional, and it does not know your market the way you do.",
            "Good jobs for AI: first drafts, summaries, organizing notes, brainstorming, rewriting for different audiences, and turning a messy list into a clear plan.",
            "Keep these in mind: AI can be wrong, sound confident while wrong, and invent details like sales numbers or school ratings. Check every fact against the MLS, the contract, or the public record before it reaches a client. Never give legal advice or interpret a contract with AI. Describe what the contract says and point your client to the title company, lender, or attorney."
          ],
          "links": []
        },
        {
          "id": "ai-protect-clients",
          "title": "Give AI the Details It Needs",
          "body": [
            "Paste in whatever the task needs: notes, listing details, emails, and documents. Resi's built-in AI tools are a great place to start.",
            "The more context you give, such as the client's goals, the property details, and past messages, the more useful the draft will be."
          ],
          "tipBox": {
            "title": "AI account safety",
            "intro": "A few quick settings keep your AI accounts working for you:",
            "items": [
              "Turn on two-factor login for your AI accounts.",
              "Use a long, unique password saved in a password manager.",
              "Use your work account for business.",
              "Turn on the setting that keeps your chats out of model training.",
              "No need to change passwords routinely; change one only if you think it leaked."
            ]
          },
          "links": []
        }
      ]
    },
    {
      "id": "everyday",
      "title": "Everyday Tasks",
      "steps": [
        {
          "id": "ai-write-faster",
          "title": "Write Faster Without Losing Your Voice",
          "body": [
            "Give AI a sample of something you already wrote, such as a past listing description or a client email, and ask it to match your tone. Then edit the draft until it sounds like you.",
            "Short, specific prompts work best. For example: \"Draft a 3-sentence text to a buyer who missed the home by one offer. Keep it warm and end with a question about their next steps.\"",
            "Good uses: follow-up emails and texts after showings, thank-you notes after closings, newsletter outlines for your farm, social captions from a listing summary, and a checklist for your next listing appointment."
          ],
          "links": []
        },
        {
          "id": "ai-email",
          "title": "Checking Email and Drafting Replies",
          "body": [
            "If your AI app is connected to your email, ask it to sort your inbox each morning into three piles: needs a reply today, needs a reply this week, and information only. Ask it to pull out any dates, deadlines, or dollar amounts so nothing gets missed.",
            "A safe prompt: \"Review my unread emails from the past day. Group them into: needs a reply today, needs a reply this week, and information only. For each one, give the sender, the subject, and any dates or dollar amounts mentioned. Don't send anything.\"",
            "Have AI draft replies, then read each one and change anything that doesn't sound like you or that promises something you haven't confirmed. Ask AI to draft, not send. Turn off any setting that lets it send email on your behalf. Every message to a client, lender, or title company should go out only after you've read it.",
            "Watch for messages that ask for personal or financial information, or that include links you didn't expect. Those can be phishing, and AI can miss them."
          ],
          "links": []
        },
        {
          "id": "ai-inspections-contracts",
          "title": "Summarizing Inspection Reports and Contracts",
          "body": [
            "A 60-page inspection report can become a one-page list of items to review, and a long contract can become a list of dates, dollar amounts, and deadlines you then check against the original.",
            "Upload the documents you're working on so the AI can read them directly. Resi's built-in AI tools are a great place to start.",
            "A safe prompt for an inspection report: \"Summarize this inspection report. List each item by category, with its severity as the report describes it, and flag any items that mention safety, active leaks, or structural concerns. Don't rate the house overall.\"",
            "A safe prompt for a contract: \"List the key dates, dollar amounts, contingencies, and responsibilities in this contract, with the section number for each. Don't interpret what any clause means legally.\"",
            "Always check the summary against the original document. AI can miss or misread details. Describe what the document says, then send the client to the inspector, title company, lender, or attorney for questions about rights or obligations."
          ],
          "links": []
        },
        {
          "id": "ai-blogs-faqs",
          "title": "Writing Blog Posts and Researching FAQs",
          "body": [
            "AI can outline a blog post, draft sections, and turn your verified notes into the questions buyers and sellers ask most. Give it your topic, your audience, and the facts you've checked, then ask for an outline first. Write the final version in your own voice.",
            "A safe prompt: \"Outline an 800-word blog post for relocation buyers moving to Southwest Florida. Use only the facts I've listed below, and mark any place where you need a verified number.\" Then paste your verified facts.",
            "Check every number, date, and local detail against a current source before you publish. Use official sources and news outlets for links, and don't link to competitor websites."
          ],
          "links": []
        },
        {
          "id": "ai-video-scripts",
          "title": "Writing Video Scripts",
          "body": [
            "Give AI your topic, your audience, and the three points you want to cover. Ask for a 60-second script, then a 3-minute version. Read it out loud and rewrite any line you wouldn't say to a friend.",
            "Keep your call to action clear, such as calling or texting you at the number on your site."
          ],
          "links": []
        },
        {
          "id": "ai-lead-magnets",
          "title": "Creating Lead Magnets",
          "body": [
            "A lead magnet is a useful resource, such as a checklist, guide, or short report, that people receive in exchange for their contact information. Ask AI to suggest five ideas for your audience, then pick one and ask for an outline. Have it draft each section, then check every fact and edit the result in your voice.",
            "Keep the offer honest and specific. Describe what the resource contains. Don't promise results or present it as a substitute for professional advice."
          ],
          "links": []
        }
      ]
    },
    {
      "id": "listings-research",
      "title": "Listings and Research",
      "steps": [
        {
          "id": "ai-listing-fair-housing",
          "title": "Listing Descriptions and Fair Housing",
          "body": [
            "AI can help write listing descriptions, but it must follow fair housing rules. Describe the home and its features, not the people who might live there. Avoid words like \"perfect for families,\" \"ideal for young professionals,\" \"exclusive,\" \"safe neighborhood,\" or \"walking distance to church.\" Those can steer buyers by family status, age, race, religion, or other protected classes.",
            "A safe prompt: \"Write an MLS description for this 3-bedroom, 2-bath home in Corkscrew Shores with a screened lanai and updated kitchen. Describe features only, no lifestyle or demographic language, and keep it under 1,000 characters.\"",
            "Read every description before you publish it."
          ],
          "links": []
        },
        {
          "id": "ai-research",
          "title": "Research and Market Updates",
          "body": [
            "Use AI to organize information you've already verified: community amenities, HOA rules you've pulled from the documents, school zone lists from the official district site, or a summary of a market report you've read.",
            "Ask AI to show its sources, then check them. If a number is soft, say so. Don't let AI round a number in your favor."
          ],
          "links": []
        }
      ]
    },
    {
      "id": "tools-routine",
      "title": "Tools, Questions, and Your Routine",
      "steps": [
        {
          "id": "ai-20-quick-wins",
          "title": "20 Quick Ways to Work Faster With AI",
          "body": [
            "1. Draft a first reply to any email. Paste the email with names and numbers removed, say what you want to say, and ask for a short, warm reply.",
            "2. Turn a voice note into a clean to-do list. Dictate what's on your mind after a showing, then ask AI to sort it into tasks with deadlines.",
            "3. Summarize long documents. Ask for key dates, dollar amounts, and open questions, then read the original sections that matter.",
            "4. Write a text for a hard conversation. Ask for three versions: direct, gentle, and short.",
            "5. Plan your prospecting week. Give AI your goals and available hours, and ask for a day-by-day plan you can adjust.",
            "6. Create a neighborhood FAQ from your verified notes, then check each answer.",
            "7. Rewrite one listing summary for a relocating buyer, a retiree, and an investor.",
            "8. Prepare for appointments. Ask which questions a seller is likely to ask, then practice your answers out loud.",
            "9. Build a thank-you note library with ten short notes for different situations.",
            "10. Make a weekly client check-in list with a reason to reach out to each client.",
            "11. Draft social posts from your own verified content, in three options.",
            "12. Organize meeting notes into decisions, action items, and owners.",
            "13. Plan five video ideas based on questions clients have asked.",
            "14. Script a 60-second video from your talking points, then rewrite it in your voice.",
            "15. Explain terms like \"earnest money\" in plain English, then check the explanation against official guidance.",
            "16. Draft a one-page offer summary from terms you've verified, then review it against the contract yourself.",
            "17. Outline a newsletter from your three best updates for the month.",
            "18. Study for licensing or continuing education. Ask for quizzes, explanations of wrong answers, and flashcards.",
            "19. Translate a client message into simple language, then have a fluent speaker check it before sending.",
            "20. Review your own writing for three ways to make it shorter and clearer, without changing your voice."
          ],
          "links": []
        },
        {
          "id": "connect-your-ai",
          "title": "Connect Your AI",
          "body": [
            "An MCP connection lets your AI app reach outside tools so you can ask for what you need in plain language instead of clicking through menus.",
            "Connect Your AI links Resi directly to the AI app you use (Claude, ChatGPT, Grok, Grok Bot, Gemini, Perplexity, or another MCP client). It can look up your deals, commission, pipeline, leads and tasks, and add or update them when you ask. Agents reach only their own data.",
            "To connect it: open your Profile in Resi, scroll to Connect Your AI, and pick your app. Each app shows a short plan note and simple numbered steps. The Resi address is https://resisystem.com/mcp. Most apps then open a Resi sign-in page: type your Resi email and password, tap Sign in, then tap Approve. If it says Unauthorized, remove Resi from the AI app and add it again.",
            "Before you connect any tool, check what it can read and what it can change. Keep the connection to what you need, and revoke anything you no longer use. Don't let an AI app make changes on your behalf until you have reviewed what it will do."
          ],
          "links": []
        },
        {
          "id": "ai-tools-site",
          "title": "Questions About a Tool or the Site",
          "body": [
            "AI is a good first stop for questions like \"How do I attach a document in this app?\" or \"Why isn't my calendar syncing?\" Describe what you see on the screen, what you clicked, and what you expected to happen. Ask for step-by-step instructions, then try them.",
            "If AI's answer doesn't match what you see, check the team's training or ask the team's support contact before trying a change that could affect client files."
          ],
          "links": []
        },
        {
          "id": "ai-contracts-law",
          "title": "Questions About Contracts and Florida Law",
          "body": [
            "AI can help you understand general terms, such as what \"earnest money\" or \"due diligence period\" means in plain English, and it can help you organize questions for your broker, title company, or attorney.",
            "It should not interpret a specific contract clause, tell a client what their rights or obligations are, or give an answer about Florida law for a particular situation. Those answers can change with the facts, and getting them wrong can hurt a client.",
            "Describe what the document says, then send the question to the right person. For Florida contract and legal questions, the team's attorney is David Ged with Heights Title. For financing questions, send them to the client's lender."
          ],
          "links": []
        },
        {
          "id": "ai-weekly-routine",
          "title": "Build Your Weekly AI Routine",
          "body": [
            "Pick three tasks you do every week and use AI for each. Keep a folder of prompts that work, and update them as you learn. A good starter routine:",
            "Monday: draft your week's social posts from listings and market news you've verified.",
            "Daily: summarize your inbox and draft replies, then review before sending.",
            "Friday: write your recap email to clients and a to-do list for next week.",
            "Track the time you save for a month. Many agents find an hour or more back each week once the routine sticks."
          ],
          "links": []
        }
      ]
    }
  ]
};
